import type { Messages, T } from '../../../messages';

/**
 * The Connections tab's one translation table: wire tokens in, words out.
 *
 * A scope, an event type, a delivery status and a feature are all tokens the
 * server, the CLI and the agreement share, and none of them is a word an
 * operator should have to read (plan §1, principle 2). They are translated
 * here and nowhere else on the tab, so a new token is one line in one map.
 *
 * **An unknown token answers with itself**, never with nothing: a newer server
 * mid-rollout can send a scope or an event this console has not heard of, and
 * a checkbox with no label is a worse answer than the token it was.
 */

/** A catalog key whose entry is a plain string — the only kind a map can hold. */
type Plain = { [K in keyof Messages]: Messages[K] extends string ? K : never }[keyof Messages];

const SCOPE: Record<string, Plain> = {
  'catalog:read': 'connections.scope.catalog:read',
  'calendar:read': 'connections.scope.calendar:read',
  'calendar:write': 'connections.scope.calendar:write',
  'clients:read': 'connections.scope.clients:read',
  'clients:preferences': 'connections.scope.clients:preferences',
  'clients:write': 'connections.scope.clients:write',
  'certificates:read': 'connections.scope.certificates:read',
  'credit:read': 'connections.scope.credit:read',
  'products:read': 'connections.scope.products:read',
  'menu:read': 'connections.scope.menu:read',
  'orders:read': 'connections.scope.orders:read',
  'orders:write': 'connections.scope.orders:write',
  'departures:read': 'connections.scope.departures:read',
  'lodging:read': 'connections.scope.lodging:read',
  'payments:write': 'connections.scope.payments:write',
};

/** What a scope lets a partner do, as a sentence. */
export function scopeSentence(t: T, scope: string): string {
  const key = SCOPE[scope];
  return key === undefined ? scope : t(key);
}

/**
 * A feature (a module) as the sentence "Turn on ___ first" names it, and as
 * the heading a group of scopes sits under in the "Make a key" dialog — the
 * console's one set of feature names (`../features.ts`).
 */
export { featureName, featureTitle as featureGroup } from '../features';

const EVENT: Record<string, Plain> = {
  'booking.created': 'connections.event.booking.created',
  'booking.updated': 'connections.event.booking.updated',
  'booking.cancelled': 'connections.event.booking.cancelled',
  'catalog.updated': 'connections.event.catalog.updated',
  'profile.updated': 'connections.event.profile.updated',
  'hours.updated': 'connections.event.hours.updated',
  'client.updated': 'connections.event.client.updated',
  'order.created': 'connections.event.order.created',
  'order.updated': 'connections.event.order.updated',
  'reservation.created': 'connections.event.reservation.created',
  'reservation.updated': 'connections.event.reservation.updated',
};

/**
 * The event types the endpoint dialog offers as boxes, in the order a partner
 * cares about them. Any other type an endpoint already filters on is kept and
 * shown by its token, so editing an endpoint never silently drops one.
 */
export const OFFERED_EVENTS = [
  'booking.created',
  'booking.updated',
  'booking.cancelled',
  'catalog.updated',
  'profile.updated',
  'hours.updated',
  'client.updated',
] as const;

export function eventName(t: T, type: string): string {
  const key = EVENT[type];
  return key === undefined ? type : t(key);
}

const DELIVERY: Record<string, Plain> = {
  queued: 'connections.history.status.queued',
  delivering: 'connections.history.status.delivering',
  delivered: 'connections.history.status.delivered',
  failed: 'connections.history.status.failed',
  exhausted: 'connections.history.status.exhausted',
};

/** What became of an update: "Delivered", "Retrying", "Gave up". */
export function deliveryWord(t: T, status: string): string {
  const key = DELIVERY[status];
  return key === undefined ? status : t(key);
}

/* ------------------------------------------------------------------ */
/* Moments                                                             */
/* ------------------------------------------------------------------ */

const UNITS: readonly [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['week', 7 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
];

/**
 * "2 minutes ago", "yesterday", "in 3 hours" — in the operator's language.
 *
 * This tab's moments are for a person asking "is it working", which is a
 * question about *how long ago*; the exact UTC instant stays one hover away
 * (`title`) for the comparison against a log line that `When` exists for.
 */
export function relative(iso: string, locale: string, now: Date = new Date()): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  const seconds = Math.round((at.getTime() - now.getTime()) / 1000);
  const format = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  for (const [unit, size] of UNITS) {
    if (Math.abs(seconds) >= size) return format.format(Math.round(seconds / size), unit);
  }
  return format.format(0, 'minute');
}

/** "Sep 30" this year, "Sep 30, 2025" otherwise. */
export function day(iso: string, locale: string, now: Date = new Date()): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  const sameYear = at.getFullYear() === now.getFullYear();
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(at);
}

/** "Tue, Sep 30, 14:05" — for a bound the operator is about to act on. */
export function moment(iso: string, locale: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(at);
}

/* ------------------------------------------------------------------ */
/* Money, for the one amount on this tab                               */
/* ------------------------------------------------------------------ */

/** Stored cents → what goes in the box: `15000` → `"150"`, `15050` → `"150.50"`. */
export function centsToInput(cents: number | null | undefined): string {
  if (cents == null) return '';
  return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
}

/**
 * The box → cents. `null` for empty (no limit), `undefined` for something that
 * is not an amount — the caller says so rather than sending a guess.
 */
export function inputToCents(text: string): number | null | undefined {
  const trimmed = text.trim().replace(/,/g, '');
  if (trimmed === '') return null;
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return undefined;
  return Math.round(Number(trimmed) * 100);
}

/** The server's refusal rule for a secret pasted where its reference goes. */
export function looksLikeSecret(value: string): boolean {
  const v = value.trim();
  return /^ys_(test|live)_/.test(v) || /^[0-9a-f]{64}$/.test(v);
}
